import { fromGqlId } from "@src/api/formatters/shopify";
import {
  FetchProductPickerProductsWithVariantsDocument,
  FetchProductPickerProductsWithVariantsQuery,
  FetchProductPickerProductsWithVariantsQueryVariables,
  InputMaybe,
} from "@src/generated";
import { shopifyGqlClient } from "@src/providers/gqlProvider";
import {
  ProductPickerProduct,
  ProductPickerProductVariant,
  ProductStatus,
} from "@src/store/slices/productPicker/types";

async function loadAllVariantsForProduct(
  productId: string,
  variants: FetchProductPickerProductsWithVariantsQuery["products"]["edges"][0]["node"]["variants"],
  variantsPageSize: InputMaybe<number | undefined>
): Promise<Array<ProductPickerProductVariant>> {
  const allVariants = variants.nodes.map((v) => {
    const media = v.media?.nodes?.[0];
    return {
      id: v.id,
      title: v.title,
      price: typeof v.price === "string" ? parseFloat(v.price) : v.price,
      compareAtPrice: v.compareAtPrice
        ? typeof v.compareAtPrice === "string"
          ? parseFloat(v.compareAtPrice)
          : v.compareAtPrice
        : null,
      availableForSale: v.availableForSale,
      image:
        media?.__typename === "MediaImage" ? (media.image?.url ?? null) : null,
      selectedOptions: v.selectedOptions.map((o) => ({
        name: o.name,
        value: o.value,
      })),
    };
  });

  let hasNextPage = variants.pageInfo.hasNextPage;
  let endCursor = variants.pageInfo.endCursor;

  while (hasNextPage && endCursor) {
    const result = await shopifyGqlClient.query<
      FetchProductPickerProductsWithVariantsQuery,
      FetchProductPickerProductsWithVariantsQueryVariables
    >({
      query: FetchProductPickerProductsWithVariantsDocument,
      variables: {
        first: 1,
        query: `id:${productId}`,
        variantsFirst: variantsPageSize,
        variantsAfter: endCursor,
      },
      fetchPolicy: "network-only",
    });

    const nextVariants = result.data.products.edges[0]?.node.variants;
    if (!nextVariants) {
      break;
    }

    allVariants.push(
      ...nextVariants.nodes.map((v) => {
        const media = v.media?.nodes?.[0];
        return {
          id: v.id,
          title: v.title,
          price: typeof v.price === "string" ? parseFloat(v.price) : v.price,
          compareAtPrice: v.compareAtPrice
            ? typeof v.compareAtPrice === "string"
              ? parseFloat(v.compareAtPrice)
              : v.compareAtPrice
            : null,
          availableForSale: v.availableForSale,
          image:
            media?.__typename === "MediaImage"
              ? (media.image?.url ?? null)
              : null,
          selectedOptions: v.selectedOptions.map((o) => ({
            name: o.name,
            value: o.value,
          })),
        };
      })
    );

    hasNextPage = nextVariants.pageInfo.hasNextPage;
    endCursor = nextVariants.pageInfo.endCursor ?? null;
  }

  return allVariants;
}

export async function loadProductsWithVariants(
  variables: FetchProductPickerProductsWithVariantsQueryVariables,
  allowIgProducts?: boolean
) {
  const result = await shopifyGqlClient.query<
    FetchProductPickerProductsWithVariantsQuery,
    FetchProductPickerProductsWithVariantsQueryVariables
  >({
    query: FetchProductPickerProductsWithVariantsDocument,
    variables,
    fetchPolicy: "network-only",
  });

  const data: ProductPickerProduct[] = await Promise.all(
    result.data.products.edges.map(async (e) => {
      const variants: ProductPickerProductVariant[] =
        await loadAllVariantsForProduct(
          e.node.id,
          e.node.variants,
          variables.variantsFirst
        );

      return {
        id: fromGqlId(e.node.id),
        title: e.node.title,
        image:
          e.node.featuredMedia?.__typename === "MediaImage"
            ? (e.node.featuredMedia.image?.url as string | undefined)
            : undefined,
        handle: e.node.handle,
        // Generated `status` is a string union (enumsAsTypes); the model uses
        // the local ProductStatus enum (identical values) for the runtime switch.
        status: e.node.status as ProductStatus,
        hasVariantsThatRequiresComponents:
          e.node.hasVariantsThatRequiresComponents,
        sellingPlanGroupCount: e.node.sellingPlanGroupsCount?.count ?? 0,
        requiresSellingPlan: e.node.requiresSellingPlan,
        isBundle:
          e.node.bundleComponents.nodes.length > 0 ||
          e.node.hasVariantsThatRequiresComponents,
        tags: e.node.tags,
        variants: variants,
        options: e.node.options?.map((o) => ({
          name: o.name,
          values: o.values,
        })),
      };
    })
  );

  // filter out ig duplicates since we can't do it with the explicit query
  if (!allowIgProducts && variables.query?.includes("collection_id")) {
    const filteredData = data.filter(
      (product) => !product.tags?.includes("price_test")
    );

    return {
      data: filteredData,
      endCursor: result.data.products.pageInfo.endCursor ?? undefined,
      hasNextPage: result.data.products.pageInfo.hasNextPage,
    };
  }

  return {
    data,
    endCursor: result.data.products.pageInfo.endCursor ?? undefined,
    hasNextPage: result.data.products.pageInfo.hasNextPage,
  };
}
